import type { ReactElement } from 'react';

import { REL_NOOPENER, REL_NOREFERRER, sanitize } from './sanitize';

/**
 * A constant string combining 'noopener' and 'noreferrer' for use in rel attributes.
 * This helps prevent security vulnerabilities in links opening in new tabs or windows.
 */
export const REL_ATTRIBUTE = `${REL_NOOPENER} ${REL_NOREFERRER}`;

/**
 * Renders a sanitized HTML string as a React paragraph element.
 * This function is designed for use with the George Design System (GDS) to safely render HTML content.
 *
 * @param {string} translatedText - The HTML string to be rendered. This is typically a translated text that may contain HTML.
 * @param {string} [dataCy=''] - An optional data-cy attribute value for testing purposes.
 * @returns {ReactElement} A React paragraph element with the sanitized HTML content.
 *
 * @example
 * // Basic usage with plain text
 * forceHtmlInGDS('Hello, World!');
 *
 * @example
 * // Usage with HTML content
 * forceHtmlInGDS('<strong>Bold text</strong> and <em>italic text</em>');
 *
 * @example
 * // Usage within a Tooltip component
 * <Tooltip
 *   content={forceHtmlInGDS(
 *     `<strong>${t('account.security.subdepotColumn')}:</strong><br/>${subSecAccount?.name}`
 *   )}
 * >
 *   <Icon assetIcon={ICON.CHECKMARK_CIRCLE} color={Icon.COLOR.BLUE_300} />
 * </Tooltip>
 *
 * @example
 * // Usage with translation function and dynamic content
 * forceHtmlInGDS(
 *   t('account.main.sCreditline.changeDescription', {
 *     overdraft: <LocalizedAmount amount={overdraft} showCurrency />
 *   })
 * );
 *
 * @remarks
 * - The `sanitize` function is used internally to clean the HTML content before rendering.
 * - This function is useful for rendering translated text that may contain HTML formatting.
 */
export function forceHtmlInGDS(translatedText: string, dataCy: string = ''): ReactElement {
    return <p data-cy={dataCy} dangerouslySetInnerHTML={{ __html: sanitize(translatedText) }} />;
}
